// "Possible new version of …" suggestions (ADR-0031) as the admin and the seller
// review them in bulk (owner ask 2026-10-15: "this very long list has to be made
// at least easier"). Pure helpers plus the one write that decides several at
// once. The compact list that uses them is ui/version-suggestions.tsx.
import { ApiError, api, errorMessage } from '../api/client';
import {
  decideVersionSuggestion,
  type VersionSuggestion,
  type VersionSuggestionAction,
} from '../api/versions';

export type SuggestionStrength = 'strong' | 'possible';

/**
 * A suggestion as the review list reads it. `why` and `strength` are optional:
 * the list works without them (no chip, a plain fallback sentence) and uses
 * them as soon as the server sends them.
 */
export type ReviewableSuggestion = VersionSuggestion & {
  /**
   * What matched. The server sends `summary` ("Same account ••4322, same
   * statement date 30 Sep 2025.") with `matchedOn`; `sentence` is accepted too.
   */
  why?: { summary?: string | null; sentence?: string | null; matchedOn?: string[] } | null;
  /** How sure the match is (server: strong | medium | weak): strong ones can be merged together in one click. */
  strength?: 'strong' | 'medium' | 'weak' | SuggestionStrength | null;
};

export const fileLabel = (name: string | null): string => name ?? 'Untitled file';

/** The one line under a row: the server's own reason, else a plain fallback. Pure. */
export function suggestionWhyText(s: ReviewableSuggestion): string {
  const raw = s.why?.summary ?? s.why?.sentence;
  const sentence = typeof raw === 'string' ? raw.trim() : '';
  return sentence || `Looks like an updated copy of ${fileLabel(s.existingDocumentName)}`;
}

/** `strong`, or `possible` for the server's medium / weak; null when it does not say (no chip). Pure. */
export function suggestionStrength(s: ReviewableSuggestion): SuggestionStrength | null {
  if (s.strength === 'strong') return 'strong';
  if (s.strength === 'medium' || s.strength === 'weak' || s.strength === 'possible') {
    return 'possible';
  }
  return null;
}

/** Strong matches first; otherwise the server's order (newest first) is kept. Pure. */
export function sortStrongFirst<T extends ReviewableSuggestion>(list: readonly T[]): T[] {
  const rank = (s: T) => (suggestionStrength(s) === 'strong' ? 0 : 1);
  return list
    .map((s, i) => [s, i] as const)
    .sort(([a, ai], [b, bi]) => rank(a) - rank(b) || ai - bi)
    .map(([s]) => s);
}

/** A copy of `from` without the `drop` keys. Pure. */
export function withoutIds<T>(set: ReadonlySet<T>, drop: Iterable<T>): Set<T>;
export function withoutIds<K, V>(map: ReadonlyMap<K, V>, drop: Iterable<K>): Map<K, V>;
export function withoutIds<K, V>(
  from: ReadonlySet<K> | ReadonlyMap<K, V>,
  drop: Iterable<K>,
): Set<K> | Map<K, V> {
  const next = from instanceof Map ? new Map(from) : new Set(from as ReadonlySet<K>);
  for (const d of drop) next.delete(d);
  return next;
}

/** The result of deciding one suggestion. */
export interface SuggestionOutcome {
  id: string;
  ok: boolean;
  /** Why it was not saved (shown on its row). */
  error?: string;
}

interface BulkResult {
  /** The server answers `suggestionId`; `id` is accepted too. */
  suggestionId?: string;
  id?: string;
  status?: string;
  code?: string;
  detail?: string;
  error?: string | { message?: string; detail?: string } | null;
}

/** The server takes at most this many ids per bulk call. */
const BULK_MAX = 100;

/** Remembers, per page load, whether the server has the bulk endpoint. */
export interface BulkSupport {
  supported: boolean | null;
}

const BULK_STATUS_TEXT: Record<string, string> = {
  not_found: 'This suggestion is no longer open.',
  forbidden: 'You can’t decide this one.',
  stale: 'This changed meanwhile — reload and try again.',
};

function bulkError(r: BulkResult): string {
  const e = r.error;
  if (typeof e === 'string' && e) return e;
  if (e && typeof e === 'object' && (e.message || e.detail)) return (e.message || e.detail)!;
  if (r.detail) return r.detail;
  if (r.status && BULK_STATUS_TEXT[r.status]) return BULK_STATUS_TEXT[r.status]!;
  return 'Not saved. Try again.';
}

async function oneByOne(
  roomId: string,
  ids: readonly string[],
  action: VersionSuggestionAction,
): Promise<SuggestionOutcome[]> {
  const out: SuggestionOutcome[] = [];
  // In turn, not all at once: each merge takes the room's versioning lock.
  for (const id of ids) {
    try {
      await decideVersionSuggestion(roomId, id, action);
      out.push({ id, ok: true });
    } catch (err) {
      out.push({ id, ok: false, error: errorMessage(err) ?? 'Not saved. Try again.' });
    }
  }
  return out;
}

/**
 * Merge or keep separate several suggestions. One call to
 * `POST /rooms/:roomId/version-suggestions/bulk` `{ action, suggestionIds }`
 * when the server has it (answer: `{ results: [{ suggestionId, status,
 * detail? }] }`); when it does not (404/405) the same decisions go one by one
 * through the single-suggestion endpoint, and `bulk` remembers that for the
 * rest of the page's life.
 */
export async function resolveVersionSuggestions(
  roomId: string,
  ids: readonly string[],
  action: VersionSuggestionAction,
  bulk: BulkSupport,
): Promise<SuggestionOutcome[]> {
  if (ids.length === 0) return [];
  if (ids.length === 1 || bulk.supported === false) return oneByOne(roomId, ids, action);
  if (ids.length > BULK_MAX) {
    const out: SuggestionOutcome[] = [];
    for (let i = 0; i < ids.length; i += BULK_MAX) {
      out.push(
        ...(await resolveVersionSuggestions(roomId, ids.slice(i, i + BULK_MAX), action, bulk)),
      );
    }
    return out;
  }
  let res: { results?: BulkResult[] } | null;
  try {
    res = await api.post<{ results?: BulkResult[] } | null>(
      `/rooms/${roomId}/version-suggestions/bulk`,
      { action, suggestionIds: ids },
    );
  } catch (err) {
    if (err instanceof ApiError && (err.status === 404 || err.status === 405)) {
      bulk.supported = false;
      return oneByOne(roomId, ids, action);
    }
    const message = errorMessage(err) ?? 'Not saved. Try again.';
    return ids.map((id) => ({ id, ok: false, error: message }));
  }
  bulk.supported = true;
  const results = res?.results;
  // A 2xx without per-item results: every one was decided.
  if (!Array.isArray(results)) return ids.map((id) => ({ id, ok: true }));
  const byId = new Map<string, BulkResult>();
  for (const r of results) {
    const rid = r.suggestionId ?? r.id;
    if (typeof rid === 'string') byId.set(rid, r);
  }
  return ids.map((id) => {
    const r = byId.get(id);
    if (r && (r.status === 'merged' || r.status === 'kept_separate')) return { id, ok: true };
    return { id, ok: false, error: r ? bulkError(r) : 'Not saved. Try again.' };
  });
}

/** "3 merged" / "2 kept separate", plus how many were not saved. Pure. */
export function outcomeSummary(
  outcomes: readonly SuggestionOutcome[],
  action: VersionSuggestionAction,
): string {
  const ok = outcomes.filter((o) => o.ok).length;
  const failed = outcomes.length - ok;
  const done = action === 'merge' ? `${ok} merged` : `${ok} kept separate`;
  if (failed === 0) return done;
  return `${done}, ${failed} not saved — see the list`;
}
